/**
 * EditableMultiSelect — jsonb-array multi-pick primitive (Phase 1.9.4 Item #4).
 *
 * Used for: payment_types (provider_organizations.payment_types jsonb-array).
 *
 * Phase 1.9.4 design plan §3 (r2 locked):
 *   - Per-chip save mutation model (no batch button). Each add/remove
 *     fires save() immediately; matches SubcategoryPicker / SpecialtyPicker
 *     UX in this repo.
 *   - When all chips removed → save(null) (NOT empty array). Preserves
 *     the cohort NULL = "not assessed" semantic.
 *   - canEdit=false → read-only chip row (no X-close, no +Add).
 *   - Empty (null OR empty array) + canEdit → "+ Add {label}" button.
 *
 * Picker UX: BottomSheet shows full vocabulary; selecting an option
 * toggles in/out (mirrors LookupPicker selection model). Auto-closes
 * on selection (single-pick at a time; user re-opens to add more).
 */

import { useState, type ReactNode } from 'react';
import { BottomSheet } from './primitives/BottomSheet';
import { Chip, CHIP_VARIANT_CLASSES } from './primitives/Chip';
import { SavingIndicator } from './SavingIndicator';
import { pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import type { ChipVariant } from './EditableSelect';

export interface EditableMultiSelectProps<T extends string> {
  /** Current saved values. NULL or empty array both render the empty-state. */
  values: T[] | null;
  /** Allowed values + display labels. Order preserved in picker. */
  options: ReadonlyArray<{ value: T; label: string }>;
  /**
   * Save callback. Receives the next array OR null (when removing the
   * last chip; preserves cohort NULL semantic per r2 §F).
   */
  onSave: (newValues: T[] | null, priorValues: T[] | null) => Promise<void>;
  /** ARIA label + "+ Add {label}" empty-state copy stem (e.g. "Payment type"). */
  label: string;
  /** Role gate. False → read-only display. */
  canEdit?: boolean;
  /** Chip variant for selected values (default: 'service'). */
  variant?: ChipVariant;
}

const SAVED_INDICATOR_MS = 1500;

export function EditableMultiSelect<T extends string>(
  props: EditableMultiSelectProps<T>,
): ReactNode {
  const {
    values,
    options,
    onSave,
    label,
    canEdit = false,
    variant = 'service',
  } = props;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [saveState, setSaveState] = useState<'saving' | 'saved' | 'error' | null>(
    null,
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const contact = useSupportContact();

  const current = values ?? [];

  async function commit(nextValues: T[]): Promise<void> {
    // Empty array → null (cohort NULL semantic).
    const payload: T[] | null = nextValues.length === 0 ? null : nextValues;
    setSaveState('saving');
    setSaveError(null);
    try {
      await onSave(payload, values);
      setSaveState('saved');
      // The picker closes on success only, so a refused pick stays open for a retry.
      setPickerOpen(false);
      setTimeout(() => setSaveState(null), SAVED_INDICATOR_MS);
    } catch (e) {
      setSaveState('error');
      setSaveError(pageWriteText(e, contact));
    }
  }

  function toggleValue(v: T): void {
    if (current.includes(v)) {
      void commit(current.filter((c) => c !== v));
    } else {
      void commit([...current, v]);
    }
  }

  function removeValue(v: T): void {
    void commit(current.filter((c) => c !== v));
  }

  // Read-only path.
  if (!canEdit) {
    if (current.length === 0) {
      return <Chip variant="neutral">{label}: not specified</Chip>;
    }
    return (
      <span className="inline-flex flex-wrap gap-1.5">
        {current.map((v) => {
          const opt = options.find((o) => o.value === v);
          return (
            <Chip key={v} variant={variant}>
              {opt?.label ?? v}
            </Chip>
          );
        })}
      </span>
    );
  }

  // Editable path.
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {current.map((v) => {
        const opt = options.find((o) => o.value === v);
        return (
          <span key={v} className="inline-flex items-center gap-1">
            <span
              className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-medium ${chipBackgroundForVariant(variant)}`}
            >
              {opt?.label ?? v}
            </span>
            <button
              type="button"
              onClick={() => removeValue(v)}
              aria-label={`Remove ${opt?.label ?? v}`}
              className="text-ink-soft hover:text-ink cursor-pointer text-xs leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded-sm"
            >
              ✕
            </button>
          </span>
        );
      })}
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        aria-label={`Add ${label}`}
        className="inline-flex items-center gap-1 rounded-full border border-dashed border-sage-300 px-2.5 py-1 text-[11px] font-medium text-sage-600 hover:bg-sage-50 hover:border-sage-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 transition-colors"
      >
        + Add {label}
      </button>
      <SavingIndicator state={saveState} errorMessage={saveError ?? undefined} />

      <BottomSheet
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={label}
      >
        <div role="listbox" className="px-5 py-3">
          {options.map((opt) => {
            const isSelected = current.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => toggleValue(opt.value)}
                className={`flex w-full items-center gap-3 py-2.5 px-1 border-b border-sage-800/[8%] last:border-0 cursor-pointer text-left ${isSelected ? 'text-sage-700 font-semibold' : 'text-ink'}`}
              >
                <span
                  className={`w-[18px] h-[18px] rounded-md border-2 flex items-center justify-center shrink-0 text-[10px] ${isSelected ? 'border-sage-400 bg-sage-800 text-cream' : 'border-sage-300'}`}
                >
                  {isSelected && '✓'}
                </span>
                <span className="text-sm">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </span>
  );
}

/** UX-13 S3 — the chip surface derives from the Chip primitive's exported
 *  variant map (CHIP_VARIANT_CLASSES). The previous hand-copied switch here
 *  drifted to the retired Tailwind hues (green/amber/blue/cyan/rose) when the
 *  primitive moved to the brand identity set — edit-mode chips rendered the
 *  old palette beside read-only brand chips. One map, no mirrors. */
function chipBackgroundForVariant(variant: ChipVariant): string {
  return CHIP_VARIANT_CLASSES[variant] ?? CHIP_VARIANT_CLASSES.neutral;
}
